// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// Account access tiers. The string values intentionally match the Neon
// `AccessTier` enum (public/student/member/advanced) already used by the
// shared user store, so a tier read from the database round-trips through
// `parseTier` without translation. Tiers are totally ordered by privilege;
// gating decisions go through `tierAtLeast` rather than comparing strings.

export enum Tier {
  Public = 'public',
  Student = 'student',
  Member = 'member',
  Advanced = 'advanced',
}

// The tier assigned to new and unauthenticated users. Also the safe fallback
// when a stored value can't be recognized: least privilege, so an unexpected
// value can never silently grant access.
export const DEFAULT_TIER: Tier = Tier.Public;

// Privilege ranking, low to high. Used only via tierAtLeast; not exported so
// callers can't accidentally compare ranks with the wrong operator.
const TIER_RANK: Readonly<Record<Tier, number>> = {
  [Tier.Public]: 0,
  [Tier.Student]: 1,
  [Tier.Member]: 2,
  [Tier.Advanced]: 3,
};

// Tiers ordered low → high privilege. Handy for UI (e.g. upgrade prompts).
export const TIERS_BY_PRIVILEGE: readonly Tier[] = [Tier.Public, Tier.Student, Tier.Member, Tier.Advanced];

export function isTier(value: unknown): value is Tier {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(TIER_RANK, value);
}

// Coerce an arbitrary stored/serialized value into a Tier, falling back to
// DEFAULT_TIER (least privilege) for anything unrecognized, including
// null/undefined. Never throws: callers in the auth path must always get a
// usable tier rather than having to handle an error mid-request.
export function parseTier(value: unknown): Tier {
  return isTier(value) ? value : DEFAULT_TIER;
}

// True when `tier` is at least as privileged as `minTier`. This is the single
// gating predicate the tier system is built on (server middleware + UI).
export function tierAtLeast(tier: Tier, minTier: Tier): boolean {
  return TIER_RANK[tier] >= TIER_RANK[minTier];
}
